// Thin wrapper around Stripe.js for saving cards (SetupIntents).
// loadStripe is called once per session (module-level promise) with the
// publishable key from runtimeConfig.public.stripePublishableKey
// (NUXT_PUBLIC_STRIPE_PUBLISHABLE_KEY).
//
// Usage (see components/payments/AddCardDialog.vue):
//   const {mountPaymentElement, confirmSetup} = useStripePayments();
//   const session = await mountPaymentElement(clientSecret, elRef.value);
//   const {error, setupIntent} = await confirmSetup(session);
import {loadStripe} from '@stripe/stripe-js';
import {useRuntimeConfig} from '#app';
import {paymentElementOptions} from '~/composables/usePayingHonestly.js';

// Keyed by connected account ('' = platform). Entry-fee PaymentIntents are
// direct charges on the club's connected account, and Stripe.js only sees
// intents belonging to the account it was initialised with — so each account
// gets its own instance. SetupIntents (saved cards) live on the platform.
const stripePromises = {};

export function useStripePayments() {
  const config = useRuntimeConfig();

  const getStripe = (stripeAccount = null) => {
    const key = stripeAccount || '';
    if (!stripePromises[key]) {
      stripePromises[key] = loadStripe(
          config.public.stripePublishableKey,
          stripeAccount ? {stripeAccount} : undefined,
      );
    }
    return stripePromises[key];
  };

  /**
   * Create an Elements group for a SetupIntent client secret and mount the
   * Payment Element (wallets first, then card) into `el`.
   * Returns {stripe, elements, paymentElement} — pass this to confirmSetup.
   * Pass options.stripeAccount for client secrets minted on a connected account.
   */
  const mountPaymentElement = async (clientSecret, el, options = {}) => {
    const {stripeAccount, savingCard = false, ...elementsOptions} = options;
    const stripe = await getStripe(stripeAccount);
    if (!stripe) {
      throw new Error('Stripe.js failed to load. Check NUXT_PUBLIC_STRIPE_PUBLISHABLE_KEY');
    }
    const elements = stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          fontFamily: 'Poppins, sans-serif',
          borderRadius: '10px',
        },
      },
      ...elementsOptions,
    });
    const paymentElement = elements.create('payment', paymentElementOptions({savingCard}));
    paymentElement.mount(el);
    return {stripe, elements, paymentElement};
  };

  /**
   * Confirm the SetupIntent with whatever the user entered in the Payment
   * Element. Stays on the page unless the method demands a redirect (3DS etc.
   * come back to returnUrl). Resolves to Stripe's {error} / {setupIntent}.
   */
  const confirmSetup = async ({stripe, elements}, returnUrl = null) => {
    return stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl ?? window.location.href,
      },
      redirect: 'if_required',
    });
  };

  /**
   * Confirm a PaymentIntent (interactive entry-fee payment). Same Element, but
   * money moves now — wallets (Apple/Google Pay) and cards. Stays on the page
   * unless the method demands a redirect. Resolves to {error} / {paymentIntent}.
   */
  const confirmPayment = async ({stripe, elements}, returnUrl = null) => {
    return stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl ?? window.location.href,
      },
      redirect: 'if_required',
    });
  };

  /**
   * DEFERRED-INTENT variant for pay pages: mount the Payment Element from just
   * {amountMinor, currency} — no PaymentIntent exists yet, so merely opening the
   * page changes nothing server-side (a charge used to flip to Processing and
   * vanish from the club's Overdue tab the moment its pay link was opened).
   * The intent is minted when the payer commits: see confirmDeferredPayment.
   */
  const mountDeferredPaymentElement = async ({amountMinor, currency, stripeAccount = null}, el) => {
    const stripe = await getStripe(stripeAccount);
    if (!stripe) {
      throw new Error('Stripe.js failed to load. Check NUXT_PUBLIC_STRIPE_PUBLISHABLE_KEY');
    }
    const elements = stripe.elements({
      mode: 'payment',
      amount: amountMinor,
      currency: String(currency).toLowerCase(),
      appearance: {
        theme: 'stripe',
        variables: {
          fontFamily: 'Poppins, sans-serif',
          borderRadius: '10px',
        },
      },
    });
    const paymentElement = elements.create('payment', {
      wallets: {link: 'never'},
    });
    paymentElement.mount(el);
    return {stripe, elements, paymentElement};
  };

  /**
   * Deferred flow, step 1: validate + collect what the payer entered. Stripe
   * requires this BEFORE the async call that mints the intent. Resolves to
   * {error} — show error.message and stop if present.
   */
  const submitDeferredPayment = async ({elements}) => elements.submit();

  /**
   * Deferred flow, step 2: confirm the freshly minted PaymentIntent with the
   * already-submitted Element. Same redirect semantics as confirmPayment.
   */
  const confirmDeferredPayment = async ({stripe, elements}, clientSecret, returnUrl = null) => {
    return stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl ?? window.location.href,
      },
      redirect: 'if_required',
    });
  };

  return {
    getStripe, mountPaymentElement, confirmSetup, confirmPayment,
    mountDeferredPaymentElement, submitDeferredPayment, confirmDeferredPayment,
  };
}
